const ELEMENT_NAME = "collapsible-group";

const classes = {
  wrapper: "collapsible-group",
  content: "collapsible-group__main",
  buttonsArea: ['collapsible-group__buttons', 'page-width', 'page-width--regular'],
  button: "button",
  nonActiveButton: "button--outline",
};

const attributes = {
  title: "data-collapsible-group-title",
  active: "active",
  link: "data-collapsible-group-button-link"
};

if (!customElements.get(ELEMENT_NAME)) {
  class CollapsibleGroup extends HTMLElement {
    $buttons = [];
    $panels = [];
    $elements = [];

    connectedCallback() {
      // If this section is already inside an accordion group, then return
      if (this.closest(`.${classes.wrapper}`)) return;

      // If this is not a first accordion group section, then return
      const $section = this.closest("[id^='shopify-section-']");
      if (!$section) return;

      const $prevSection = $section.previousElementSibling;
      if ($prevSection && $prevSection.querySelector(ELEMENT_NAME)) return;

      // Create a wrapper for the accordion group
      const $wrapper = document.createElement("DIV");
      $wrapper.classList.add(classes.wrapper);

      const $buttonsArea = document.createElement("DIV");
      $buttonsArea.classList.add(...classes.buttonsArea);
      $buttonsArea.setAttribute("role", "tablist");

      const $content = document.createElement("DIV");
      $content.classList.add(classes.content);

      $wrapper.append($buttonsArea, $content);

      $section.insertAdjacentElement("beforebegin", $wrapper);

      // Add all the adjacent accordion sections to the content element of the wrapper
      // And save section titles
      let $nextSection = $wrapper.nextElementSibling;
      let $element;
      let counter = 0;
      while ($nextSection && ($element = $nextSection.querySelector(ELEMENT_NAME))) {
        $content.append($nextSection);
        this.$elements.push($element);

        const tabId = `collapsible-tab-${counter}`;
        const panelId = `collapsible-panel-${counter}`;

        // Set up panel
        $nextSection.setAttribute("role", "tabpanel");
        $nextSection.setAttribute("id", panelId);
        $nextSection.setAttribute("aria-labelledby", tabId);
        this.$panels.push($nextSection);

        const $title = $nextSection.querySelector(`[${attributes.title}]`);
        if ($title) {
          const $button = document.createElement("BUTTON");
          $button.innerHTML = $title?.innerHTML || "";
          $button.setAttribute("type", "button");
          $button.setAttribute("role", "tab");
          $button.setAttribute("id", tabId);
          $button.setAttribute("aria-controls", panelId);
          $button.setAttribute("aria-selected", "false");
          $button.setAttribute("tabindex", "-1");
          $button.classList.add(classes.button, classes.nonActiveButton);
          const currentCounter = counter;

          // Set active section by url param
          const activeTabId = this.getTabId();
          if (activeTabId === counter + 1) {
            $button.classList.remove(classes.nonActiveButton);
            $button.setAttribute("aria-selected", "true");
            $button.setAttribute("tabindex", "0");
            $element.setAttribute(attributes.active, "");
          }

          $button.addEventListener("click", () => {
            this.toggle(currentCounter);
          });

          $button.addEventListener("keydown", (e) => {
            this.#handleKeydown(e, currentCounter);
          });

          this.$buttons.push($button);
          $buttonsArea.insertAdjacentElement("beforeend", $button);
        } else {
          const $link = $nextSection.querySelector(`[${attributes.link}]`);
          if ($link) {
            const $linkButton = document.createElement("A");
            $linkButton.innerHTML = $link?.innerHTML || "";
            const $linkUrl = $link.getAttribute('href');
            $linkButton.setAttribute("href", $linkUrl);
            $linkButton.setAttribute("role", "tab");
            $linkButton.setAttribute("id", tabId);
            $linkButton.setAttribute("aria-controls", panelId);
            $linkButton.setAttribute("aria-selected", "false");
            $linkButton.setAttribute("tabindex", "-1");
            $linkButton.classList.add(classes.button, classes.nonActiveButton);

            this.$buttons.push($linkButton);
            $buttonsArea.insertAdjacentElement("beforeend", $linkButton);
          }
        }

        $nextSection = $wrapper.nextElementSibling;
        counter++;
      }

      // If no tab was activated by URL param, activate the first one
      if (!this.$buttons.some(b => b.getAttribute("aria-selected") === "true")) {
        this.toggle(0);
      }
    }

    toggle(index) {
      this.$buttons.forEach(($button, i) => {
        $button.classList.add(classes.nonActiveButton);
        $button.setAttribute("aria-selected", "false");
        $button.setAttribute("tabindex", "-1");
        this.$elements[i]?.removeAttribute(attributes.active);

        if (i === index) {
          $button.classList.remove(classes.nonActiveButton);
          $button.setAttribute("aria-selected", "true");
          $button.setAttribute("tabindex", "0");
          this.$elements[i]?.setAttribute(attributes.active, "");
          this.setTabId(index + 1);
        }
      });
    }

    #handleKeydown(e, currentIndex) {
      let newIndex;
      const tabCount = this.$buttons.length;

      switch (e.key) {
        case 'ArrowRight':
          newIndex = (currentIndex + 1) % tabCount;
          break;
        case 'ArrowLeft':
          newIndex = (currentIndex - 1 + tabCount) % tabCount;
          break;
        case 'Home':
          newIndex = 0;
          break;
        case 'End':
          newIndex = tabCount - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      this.toggle(newIndex);
      this.$buttons[newIndex].focus();
    }

    getTabId() {
      const urlParams = new URLSearchParams(window.location.search);
      const tabId = urlParams.get('tab');
      return tabId ? +tabId : 0;
    }

    setTabId(val) {
      let url = new URL(window.location.href);
      let params = new URLSearchParams(url.search);
      params.set('tab', val);
      url.search = params.toString();
      window.history.pushState({}, '', url);
    }
  }

  customElements.define(ELEMENT_NAME, CollapsibleGroup);
}
